import React, { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react';

import { graphql, useStaticQuery } from 'gatsby';

import theme from 'styles/theme';

import HeaderLogo from 'components/HeaderLogo/HeaderLogo';
import {
  MainNav,
  MegaMenuItems,
  MobileNav,
  Search,
  Sidebar,
  SuperNav,
  SuperNavButton,
} from 'components/Layout/Header/components';
import AlertBanner from 'components/Layout/Header/components/AlertBanner';

import useScroll from 'hooks/useScroll';
import useWindowDimensions from 'hooks/useWindowDimensions';

import { AlertBannerBlockI } from 'interfaces/content-stack/blocks/alertBanner';
import { HeaderI } from 'interfaces/content-stack/header';

import { getModularBlockLinkData } from 'utils/modularBlockUtils';

interface HeaderProps {
  isAlertOpen: boolean;
  setIsAlertOpen: Dispatch<SetStateAction<boolean>>;
  contentstackAlertBanner: AlertBannerBlockI;
}

const Header: React.FC<HeaderProps> = ({ isAlertOpen, setIsAlertOpen, contentstackAlertBanner }) => {
  const [showSearchBar, setShowSearchBar] = useState<boolean>(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const { width } = useWindowDimensions();
  const isMobile = width <= theme.breakpoints.values.lg;
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const { scrollDirection } = useScroll();
  const [alertHeight, setAlertHeight] = useState<number>(0);

  useEffect(() => {
    if (isSidebarOpen && isMobile) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = 'unset';
    }
  }, [isSidebarOpen, isMobile]);

  const { contentstackHeader }: { contentstackHeader: HeaderI } = useStaticQuery(graphql`
    query {
      contentstackHeader {
        ...HeaderFragment
      }
    }
  `);
  const { primaryNav, superNav } = contentstackHeader;

  return (
    <>
      <AlertBanner
        data={contentstackAlertBanner}
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        setAlertHeight={setAlertHeight}
      />
      <SuperNav scrollDirection={scrollDirection} alertOpen={isAlertOpen}>
        {superNav?.menuItems?.map(({ linkText, link, specialBehavior }, i: number) => {
          const { link: linkUrl, target } = getModularBlockLinkData(link);

          return (
            <Fragment key={i}>
              <SuperNavButton text={linkText} specialBehavior={specialBehavior} link={linkUrl} target={target} />
            </Fragment>
          );
        })}
      </SuperNav>
      <MainNav setShow={setShowSearchBar} scrollDirection={scrollDirection} alertOpen={isAlertOpen}>
        <HeaderLogo />
        <MegaMenuItems items={primaryNav?.menuItems} />
      </MainNav>
      <Search
        placeholder={primaryNav?.searchPlaceholder}
        show={showSearchBar}
        setShow={setShowSearchBar}
        scrollDirection={scrollDirection}
        alertOpen={isAlertOpen}
        alertHeight={alertHeight}
      />
      <MobileNav
        isSidebarOpen={isSidebarOpen}
        alertHeight={alertHeight}
        toggleSidebar={toggleSidebar}
        setShowSearchBar={setShowSearchBar}
        alertOpen={isAlertOpen}
        data={contentstackHeader?.mobileNav?.navbarItems}
      />
      <Sidebar
        setIsOpen={() => setIsSidebarOpen(false)}
        isOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        menuItems={contentstackHeader?.mobileNav?.menuItems}
        alertOpen={isAlertOpen}
        alertHeight={alertHeight}
      />
    </>
  );
};

export default Header;
